// ***********************************************************
// pathname: /v1/job/{jobSlug}/phone-apply/send-code
// ***********************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useTranslation } from 'next-i18next';

type SendCodeWrite = ApiSchema<'PhoneApplyCodeWrite'>;
type SendCodeResponse = ApiSchema<'PhoneApplyCodeResponse'>;

interface UseSwrSendPhoneApplyCodeProps {
  locale: string;
  jobSlug: string;
  onSuccess?: (data: SendCodeResponse) => void;
  onError?: () => void;
}

/**
 * @description Triggers the guest send-code endpoint of the phone-apply
 * flow. Returns the standard SWR mutation tuple; pass the
 * `PhoneApplyCodeWrite` payload to `trigger()`.
 *
 * See docs/plans/phone-apply-flow.md.
 */
export const useSwrSendPhoneApplyCode = (props: UseSwrSendPhoneApplyCodeProps) => {
  const {
    locale, jobSlug, onSuccess, onError,
  } = props;

  const { t } = useTranslation('common');

  return useSwrMutationApi<SendCodeResponse, SendCodeWrite>({
    url: `/v1/job/${jobSlug}/phone-apply/send-code`,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: (data) => {
        if (onSuccess && data) onSuccess(data);
      },
      onError: () => {
        if (onError) onError();
      },
    },
    toastsOptions: {
      showSuccessToast: false,
      showErrorToast: true,
      byErrorCodes: [
        { code: '429', text: t('phone_apply.error.rate_limited', 'Too many attempts. Please try again later.') },
      ],
    },
  });
};
